import React, { useState } from "react";
import { db, auth } from "../firebase";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Checkbox,
  FormControlLabel,
  Divider,
  Paper,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";
import shiftworksLogo from "../assets/shiftworks-header-logo.svg";
import shiftworksMainLogo from "../assets/shiftworks-logo.svg";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setResetMessage("");
    try {
      // normalize email input (trim + lower) to avoid casing/whitespace mismatches
      const normalizedEmail = (email || '').trim().toLowerCase();

      // 1) Sign in with Firebase Authentication (use normalized email)
      const cred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const uid = cred.user.uid;

      // 2) Ensure ID token is available/propagated (helps rules that rely on auth.token)
      // Wait for custom claims to be set by the syncEmployeeAuth Cloud Function
      let customClaimsReady = false;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          await cred.user.getIdToken(true); // Force token refresh
          const tokenResult = await cred.user.getIdTokenResult();
          
          // Check if custom claims are set (support both role and permissionLevel claim keys)
          const claimRole = tokenResult.claims.role || tokenResult.claims.permissionLevel;
          if (tokenResult.claims.tenantId && claimRole) {
            customClaimsReady = true;
            console.log('✅ Custom claims ready:', tokenResult.claims);
            break;
          }
          
          // Wait before retry (exponential backoff: 500ms, 1s, 2s, 4s)
          if (attempt < 4) {
            console.log(`⏳ Waiting for custom claims (attempt ${attempt + 1}/5)...`);
            await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt)));
          }
        } catch (tokenErr) {
          console.warn('Token refresh attempt failed:', tokenErr);
        }
      }
      
      if (!customClaimsReady) {
        console.warn('⚠️ Custom claims not set after 5 attempts. Proceeding anyway...');
      }

      // Read the userTenants mapping to determine tenantId
      const { doc, getDoc, collection, query, where, getDocs } = await import('firebase/firestore');
      const userTenantRef = doc(db, `userTenants/${uid}`);
      const userTenantSnap = await getDoc(userTenantRef);
      if (!userTenantSnap.exists()) {
        setError('No tenant mapping found for this account. Please contact the administrator.');
        return;
      }
      const ut = userTenantSnap.data();
      const tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
      if (!tenantId) {
        setError('Tenant mapping is invalid.');
        return;
      }

      // 3) Query the tenant-scoped employees collection for this email
      const employeesRef = collection(db, `tenants/${tenantId}/employees`);
      const q = query(employeesRef, where('email', '==', normalizedEmail));
      const querySnapshot = await getDocs(q);

      // allow fallback by UID if no email-match exists (handles cases where employee docs use UID as id)
      let employeeDoc = null;
      if (querySnapshot.empty) {
        // Try UID-based doc id fallback
        try {
          const fallbackRef = doc(db, `tenants/${tenantId}/employees`, uid);
          const fallbackSnap = await getDoc(fallbackRef);
          if (fallbackSnap.exists()) {
            employeeDoc = fallbackSnap;
          } else {
            setError('No account found for this tenant with this email address.');
            return;
          }
        } catch (fbErr) {
          console.error('Employee lookup fallback error:', fbErr);
          setError('Unable to verify employee record for this account.');
          return;
        }
      } else {
        employeeDoc = querySnapshot.docs[0];
      }
  const employeeData = employeeDoc.data();
  // normalize permissionLevel to lowercase to tolerate Console edits like "Owner"
  const permissionLevel = (employeeData.permissionLevel || 'employee').toString().toLowerCase();

  // Check if user has admin/management permissions
  const allowedAdmins = ['owner', 'manager', 'schedule_manager'];
  if (!allowedAdmins.includes(permissionLevel)) {
        setError("Access denied. Please use the Employee Login to access your dashboard.");
        return;
      }

      // Store login state (you might want to use a more secure method)
      localStorage.setItem('adminUser', JSON.stringify({
        id: employeeDoc.id,
        email: employeeData.email,
        name: `${employeeData.firstName} ${employeeData.lastName}`,
        permissionLevel: permissionLevel,
        tenantId
      }));

      // Trigger theme reload for user-specific preferences
      if (window.reloadUserTheme) {
        window.reloadUserTheme();
      }

      // Redirect by role so restricted roles land on allowed pages.
      if (permissionLevel === 'schedule_manager') {
        window.location.href = "/schedule-creation";
      } else {
        window.location.href = "/dashboard";
      }
      
    } catch (err) {
      console.error('Login error:', err);
      // Surface auth-specific messages when available
      if (err && err.code && err.code.startsWith('auth/')) {
        if (err.code === 'auth/user-not-found') setError('No user found with that email.');
        else if (err.code === 'auth/wrong-password') setError('Invalid email or password.');
        else setError(err.message || 'Authentication failed.');
      } else if (err && err.code) {
        // Firestore errors often have codes like 'permission-denied'
        setError(`${err.code}: ${err.message || 'Login failed.'}`);
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");

    const normalizedEmail = (email || "").trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Enter your email first, then click Forgot password.");
      return;
    }

    setIsSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      setResetMessage("If an account exists for that email, a reset link has been sent.");
    } catch (err) {
      if (err?.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("Too many reset attempts. Please wait a few minutes and try again.");
      } else {
        setError("Could not send reset email right now. Please try again.");
      }
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "#f8f9fa",
        color: "#000",
        "& *": {
          colorScheme: "light !important",
        },
      }}
    >
      {/* Left Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 4,
          py: 8,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxWidth: 440,
            p: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            bgcolor: "#ffffff",
            color: "#000",
            "& .MuiTextField-root": {
              "& .MuiOutlinedInput-root": {
                bgcolor: '#ffffff',
                color: '#000',
              },
            },
            "& .MuiTypography-root": {
              color: '#000 !important',
            },
            "& .MuiFormLabel-root": {
              color: '#666 !important',
            },
          }}
        >
          {/* Logo/Brand */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Box sx={{ mb: 2 }}>
              <img 
                src={shiftworksLogo} 
                alt="ShiftWorks" 
                style={{ 
                  height: '64px', 
                  width: 'auto',
                  display: 'block',
                  margin: '0 auto'
                }} 
              />
            </Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 600, 
              color: '#1a1a1a',
              letterSpacing: '-0.01em',
              mb: 0.5
            }}>
              Welcome back
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6b7280'
            }}>
              Admin & Management Portal
            </Typography>
          </Box>

          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "#fee",
                borderRadius: 2,
                border: "1px solid #fcc",
              }}
            >
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          {resetMessage && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "#eefcf3",
                borderRadius: 2,
                border: "1px solid #b8ebc7",
              }}
            >
              <Typography variant="body2" sx={{ color: "#166534 !important" }}>
                {resetMessage}
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleLogin}>
            {/* Email Field */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: "#374151",
                }}
              >
                Email
              </Typography>
              <TextField
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#fff",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>

            {/* Password Field */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: "#374151",
                }}
              >
                Password
              </Typography>
              <TextField
                type="password"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#fff",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>

            {/* Forgot Password Link */}
            <Box sx={{ mb: 3, textAlign: "right" }}>
              <Link
                component="button"
                type="button"
                onClick={handleForgotPassword}
                variant="body2"
                sx={{
                  color: "#3b82f6",
                  textDecoration: "none",
                  opacity: isSendingReset ? 0.6 : 1,
                  pointerEvents: isSendingReset ? "none" : "auto",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {isSendingReset ? "Sending reset..." : "Forgot password?"}
              </Link>
            </Box>

            {/* Remember Me Checkbox */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: "#6b7280",
                      "&.Mui-checked": {
                        color: "#3b82f6",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    Keep Me Logged In
                  </Typography>
                }
              />
            </Box>

            {/* Login Button */}
            <Button
              variant="contained"
              fullWidth
              type="submit"
              sx={{
                mb: 3,
                py: 1.5,
                borderRadius: 2,
                bgcolor: "#1e40af",
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1d4ed8",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                },
              }}
            >
              Login
            </Button>

            {/* Divider */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="body2" sx={{ mx: 2, color: "#6b7280" }}>
                OR
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Social Login Buttons */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AppleIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: "#e5e7eb",
                  color: "#374151",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#d1d5db",
                    bgcolor: "#f9fafb",
                  },
                }}
              >
                Sign in with Apple
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: "#e5e7eb",
                  color: "#374151",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#d1d5db",
                    bgcolor: "#f9fafb",
                  },
                }}
              >
                Sign in with Google
              </Button>
            </Box>
          </Box>

          {/* Employee Login Link */}
          <Box
            sx={{
              textAlign: "center",
              pt: 2,
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Employee?{" "}
              <Link
                component={RouterLink}
                to="/employee/login"
                sx={{
                  color: "#3b82f6",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Right Side - Marketing Content */}
      <Box
        sx={{
          flex: 1,
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 6,
          py: 8,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)
          `,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            color: "white",
            maxWidth: 500,
          }}
        >
          {/* Logo/Icon */}
          <Box
            sx={{
              mb: 6,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img 
              src={shiftworksMainLogo} 
              alt="ShiftWorks" 
              style={{ 
                height: '120px', 
                width: 'auto'
              }} 
            />
          </Box>

          {/* Main Heading */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Restaurant Scheduling Made Easy
          </Typography>

          {/* Description */}
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              opacity: 0.9,
              lineHeight: 1.5,
              fontWeight: 400,
            }}
          >
            Streamline your staff scheduling, manage shifts effortlessly, and keep
            your team organized with our powerful restaurant management platform.
          </Typography>

          {/* CTA Button */}
          <Button
            variant="contained"
            onClick={() => navigate('/onboarding')}
            size="large"
            sx={{
              px: 4,
              py: 2,
              borderRadius: 3,
              bgcolor: "#ff6b35",
              color: "white",
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 20px rgba(255, 107, 53, 0.3)",
              "&:hover": {
                bgcolor: "#e55a2b",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 25px rgba(255, 107, 53, 0.4)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Get Started
          </Button>

          {/* Features List */}
          <Box sx={{ mt: 6, textAlign: "left" }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600 }}
            >
              Key Features:
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {[
                "Smart shift scheduling & optimization",
                "Real-time staff availability tracking",
                "Automated payroll integration",
                "Mobile app for staff communication",
              ].map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#ff6b35",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
